import { supabase } from '../../shared/supabase/supabaseClient';
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { registerStorefrontReadModelInvalidator } from '../../shared/ports/readModelInvalidationPort';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';
import {
  parseBusinessHours,
  type BusinessHourDay,
} from '../../settings/domain/storeProfileTypes';

/**
 * Evidencia remota de activación de tienda (`store_activation_snapshot` RPC).
 * Solo se llama cuando alguna card de onboarding de tienda puede mostrarse
 * (ver useStoreActivationEvidence). Una llamada reemplaza los ~8 reads que hoy
 * hacen por separado el checklist, semana-1 y setup online.
 */

const SNAPSHOT_TTL_MS = 15_000;

export type StoreActivationProfile = {
  isPublic: boolean;
  slug: string | null;
  deliveryEnabled: boolean;
  ownDeliveryEnabled: boolean;
  vueltoRidersEnabled: boolean;
  businessHours: BusinessHourDay[];
};

export type StoreActivationRemoteSnapshot = {
  generatedAt: string | null;
  profile: StoreActivationProfile | null;
  zonesCount: number | null;
  onlineVisibleProducts: number | null;
  onlineOrdersLifetime: number | null;
  lifetimeSales: number | null;
  /** snake_case: mismo shape que mp_integration_status_get / getMpIntegrationStatus. */
  mp: { oauth_connected: boolean; pos_configured: boolean } | null;
  employeesOnBranch: number | null;
  pendingInvites: number | null;
};

const asObj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const asNum = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const asStr = (v: unknown): string | null => (typeof v === 'string' ? v : null);

export function parseStoreActivationSnapshot(data: unknown): StoreActivationRemoteSnapshot | null {
  const root = asObj(data);
  if (!root) return null;

  const profile = asObj(root.profile);
  const mp = asObj(root.mp);

  return {
    generatedAt: asStr(root.generated_at),
    profile: profile
      ? {
          isPublic: profile.is_public === true,
          slug: asStr(profile.slug),
          deliveryEnabled: profile.delivery_enabled === true,
          ownDeliveryEnabled: profile.own_delivery_enabled === true,
          vueltoRidersEnabled: profile.vuelto_riders_enabled === true,
          businessHours: parseBusinessHours(profile.business_hours),
        }
      : null,
    zonesCount: asNum(root.zones_count),
    onlineVisibleProducts: asNum(root.online_visible_products),
    onlineOrdersLifetime: asNum(root.online_orders_lifetime),
    lifetimeSales: asNum(root.lifetime_sales),
    mp: mp
      ? {
          oauth_connected: mp.oauth_connected === true,
          pos_configured: mp.pos_configured === true,
        }
      : null,
    employeesOnBranch: asNum(root.employees_on_branch),
    pendingInvites: asNum(root.pending_invites),
  };
}

const cache = createKeyedTtlCache(SNAPSHOT_TTL_MS);

export async function getStoreActivationSnapshot(
  branchId: string,
  opts?: { force?: boolean },
): Promise<StoreActivationRemoteSnapshot | null> {
  return cache.get(
    branchId,
    async () => {
      const { data, error } = await supabase.rpc('store_activation_snapshot', {
        p_branch_id: branchId,
      });
      if (error) throw new Error(error.message);
      return parseStoreActivationSnapshot(data);
    },
    { force: opts?.force },
  );
}

export function invalidateStoreActivationSnapshot(branchId?: string): void {
  if (!branchId) {
    cache.clear();
    return;
  }
  cache.invalidate(branchId);
}

export function clearStoreActivationSnapshotCache(): void {
  cache.clear();
}

// Self-registration: perfil, zonas y MP son secciones de este snapshot —
// cualquier scope invalida la entrada de la branch.
registerStorefrontReadModelInvalidator((branchId) => {
  invalidateStoreActivationSnapshot(branchId ?? undefined);
});
registerSessionCacheClearHandler(clearStoreActivationSnapshotCache);
